// src/pages/Creatives.jsx
import React from 'react';
import { Box, Typography, Button, Container, Grid, Card, CardContent, Link } from '@mui/material';

// const brandYellow = '#FCCB26';

const Section = ({ title, children, bg, color }) => (
    <Box py={8} bgcolor={bg || 'white'} color={color || 'black'}>
        <Container>
            {title && (
                <Typography variant="h4" fontWeight="bold" mb={4} textAlign="center">
                    {title}
                </Typography>
            )}
            {children}
        </Container>
    </Box>
);

// #ea8002
// #ee9f41
// #f5bf7f
// #f9debe

const Brands = () => {
    return (
        <Box>

            {/* Hero Section with Background Image and Blur */}
            <Box
                sx={{
                    position: 'relative',
                    height: '95vh',
                    color: 'white',
                    overflow: 'hidden',
                }}
            >
                {/* Background Image */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        backgroundImage: 'url(/assets/servicesPic/brands2.jpg)', // ← Replace with your actual image
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        filter: 'brightness(0.5) blur(2px)',
                        zIndex: 0,
                    }}
                />

                {/* Content Over Background */}
                <Box
                    sx={{
                        position: 'relative',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexDirection: 'column',
                        zIndex: 1,
                        textAlign: 'center',
                        px: 3,
                    }}
                >
                    <Typography variant="h2" fontWeight="bold" mb={2}>
                        🏢 Higher Low for Brands & Agencies
                    </Typography>
                    <Typography variant="h5" sx={{ color: '#FCCB26' }} gutterBottom>
                        Production That Moves at the Speed of Business.
                    </Typography>
                    <Typography variant="body1" maxWidth="md" fontSize={20}>
                        You bring the vision. We handle the execution. Whether you're launching a new product, running a campaign, or building a brand — Higher Low offers production and creative solutions that align with your strategy, budget, and timeline.
                    </Typography>
                </Box>
            </Box>



            {/* What We Do Section */}
            <Section title="What We Do for Businesses" bg='#f9debe'>
                <Grid container spacing={4}>
                    {[
                        ['Full-Service Production', 'From ideation to delivery — branded content, commercials, social campaigns, testimonials, launches.'],
                        ['Creative Development', 'We help shape your story — scripting, storyboarding, lookbooks, treatments, and visual direction.'],
                        ['Agile Campaign Execution', 'Short deadlines, evolving needs — we’re built to adapt. Fast-turnaround shoots with professional results.'],
                        ['Departmental Support', 'Already working with a team? We plug in — DP, editor, director, sound, color, etc.'],
                        ['Content Strategy Alignment', 'We don’t just shoot. We align with marketing, audience, and sales goals from the start.']
                    ]
                        .map(([title, desc], idx) => (
                            <Grid item xs={12} md={6} key={idx}>
                                <Box p={3} bgcolor="#ea8002" borderRadius={2} boxShadow={2}>
                                    <Typography variant="h6" fontWeight="bold">{title}</Typography>
                                    <Typography variant="body2">{desc}</Typography>
                                </Box>
                            </Grid>
                        ))}
                </Grid>
            </Section>

            {/* How It Works Section */}
            <Section title="How It Works" bg="#ee9f41">
                <Grid container spacing={4}>
                    {[
                        ['Send a Brief or Inquiry', 'Campaign goal, target audience, budget, and timeline — we take it from there.'],
                        ['Assemble the Right Team', 'We build a custom crew and creative setup based on your needs — no bloated teams or overkill.'],
                        ['Deliver at Standard or Scale', "Whether it's one hero video or a content batch for multiple platforms — we deliver at the quality level your brand requires."]
                    ].map(([title, desc], idx) => (
                        <Grid item xs={12} md={4} key={idx}>
                            <Box p={2} bgcolor="#f5bf7f" borderRadius={2} boxShadow={1}>
                                <Typography variant="h6" fontWeight="bold">{title}</Typography>
                                <Typography variant="body2">{desc}</Typography>
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            </Section>

            {/* Why Higher Low Section (Improved) */}
            <Section title="Why Higher Low?" bg="#f5bf7f">
                <Grid container spacing={3}>
                    {[
                        'Boutique-level service with full-scale capability',
                        'Plug-and-play production across departments',
                        'Fast, reliable, and strategic with deadlines',
                        'Transparent, clear communication from start to finish',
                        'Flexible packages for one-offs or long-term'
                    ].map((text, index) => (
                        <Grid item xs={12} sm={6} md={4} key={index}>
                            <Card elevation={7} sx={{ height: '100%', borderRadius: 2 , backgroundColor: '#ee9f41'}}>
                                <CardContent>
                                    <Typography variant="body1" fontWeight="medium">
                                        {text}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </Section>

            {/* Call To Action Section (Centered + Popped) */}
            <Section bg="#f9debe">
                <Box textAlign="center">
                    <Typography variant="h4" fontWeight="bold" mb={3}>
                        Start Something
                    </Typography>
                    <Typography variant="h6" mb={4}>
                        If you’re ready to make, grow, or test something creatively — we’re here.
                    </Typography>
                    <Box display="flex" justifyContent="center" gap={3} flexWrap="wrap" mb={4}>
                        <Button
                            component="a"
                            href="mailto:shahrooz@higherlowfilms.com?subject=Project Brief Submission"
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="contained"
                            size="large"
                            sx={{
                                bgcolor: '#ea8002',
                                color: 'black',
                                px: 4,
                                py: 1.5,
                                fontSize: '1.1rem',
                                fontWeight: 600,
                                boxShadow: 4,
                                '&:hover': { bgcolor: '#ee9f41' },
                            }}
                        >
                            Submit Your Project Brief
                        </Button>

                        <Button
                            component="a"
                            href="https://calendly.com/higherlowfilms/30min"
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="outlined"
                            size="large"
                            sx={{
                                borderColor: '#ea8002',
                                color: '#ea8002',
                                px: 4,
                                py: 1.5,
                                fontSize: '1.1rem',
                                fontWeight: 600,
                                boxShadow: 2,
                                '&:hover': { borderColor: '#ee9f41', color: '#ee9f41' },
                            }}
                        >
                            Book a Free Call
                        </Button>
                    </Box>

                    <Typography variant="body1">
                    👀 Want to explore other paths? Check out our work with{' '}
                        <Link href="/services/studios" underline="hover" fontWeight="bold">
                            Studios
                        </Link>{' '}
                        –{' '}
                        <Link href="/services/creatives" underline="hover" fontWeight="bold">
                            Creatives
                        </Link>{' '}
                        –{' '}
                        <Link href="/services/supports" underline="hover" fontWeight="bold">
                            Supports
                        </Link>{' '}
                        →
                    </Typography>

                    
                </Box>

                <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: 4,
                    flexWrap: 'wrap',
                    mt: 6,
                    mb: 8, // space from footer
                    px: 2,
                }}
            >
                <Box
                    sx={{
                        width: 500,
                        height: 360,
                        borderRadius: 4,
                        overflow: 'hidden',
                        boxShadow: 3,
                        backgroundColor: '#eee',
                        backgroundImage: 'url(/assets/servicesPic/brands.jpg)', // Replace with actual image
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                    }}
                />

                <Box
                    sx={{
                        width: 500,
                        height: 360,
                        borderRadius: 4,
                        overflow: 'hidden',
                        boxShadow: 3,
                        backgroundColor: '#eee',
                        backgroundImage: 'url(/assets/servicesPic/brands1.jpg)', // Replace with actual image
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                    }}
                />
            </Box>
            </Section>

            



        </Box>


    );
};

export default Brands;
